// BILL CHECK — one set of consistency checks for a tax bill (Philip 2026-10-22, docs/bill-check-plan.md). The Document
// Scanner's Validate step, the Approvals screen and the Live Reporting "Bill Check" card all call checkBill, so the
// three can't disagree. Pure: everything comes in as arguments, nothing is stored — the flags are recomputed each time.
//
// A flag: { code, label, severity, offBy, unit, detail }
//   severity  'problem' — the bill's own figures disagree; 'check' — a guess (a line name that reads like a levy)
//   offBy     how far off, in `unit` ('$' or '%'), always positive; the report sorts its worklist on the $ size.
//             unit null (offBy 0): a flag with no size, e.g. no_rate
//
// ⛔ KNOWN-CORRECT CASES ARE NOT FLAGGED (Philip):
//   • Florida's early-payment discount — the amount due being the discounted tier is right; only a PRINTED TOTAL equal
//     to a tier is flagged (printed_discounted)
//   • supplementals — no printed-total or rate check (a prorated bill); the line checks still run
//   • Nevada's abatement — stored on the Taxes tab as each installment's Adj override (parcel_taxes, adj_type
//     'Abatement', dollars; the Las Vegas backfill). Σ ad valorem lines − the collector-year's abatements =
//     total_ad_valorem (2021: 23,217.04 − 8,412.34 = 14,804.70). The caller passes the abatement in.
//   • Illinois (Cook) — the rate check is skipped: the bill keeps its printed Local Tax Rate, and its TIF line is on a
//     different base, so the line rates don't sum to it
//   • a $0 bill (exempt) has no rate and isn't flagged for it
// Missing installments are NOT a bill check (a bill can't know its siblings); the report lists them on their own.
//
// ⛔ EVERY BILL ENDS UP WITH A RATE (Philip 2026-10-27): its line rates, its printed total rate, or its effective rate
// (ad valorem ÷ taxable, reports/taxRate effectiveBillRate). A bill that can produce none is flagged 'no_rate'.
import { parseRateCell, resolveRateUnit, printedBillRate, effectiveBillRate, isSupplementalBill } from '../reports/taxRate';
import { stateBillUnit } from './rateUnits';
import { isNonAdValorem } from './billLines';
import { moneyNum } from './money';

export const MONEY_TOLERANCE = 0.05;
export const RATE_TOLERANCE = 0.0005;     // percent
const ABATEMENT = 'Abatement';
// A line with no rate whose name reads like a property-tax levy (Philip, decision B — the report's guess for stored
// bills, which carry no section mark). LA's "VOTED INDEBTEDNESS - CITY-LOS ANGELES" lost its rate on 8940-436-272 /
// 2022 and was counted as non-ad valorem.
const LEVY_NAME = /\b(DEBT|INDEBTEDNESS|LEVY|SCHOOLS?|COLLEGE|COUNTY|CITY OF)\b/i;

const num = (v) => moneyNum(v);

// The collector-year's abatement dollars from its parcel_taxes rows: each installment's Adj override typed Abatement.
export function abatementOf(parcelTaxRows) {
  return (Array.isArray(parcelTaxRows) ? parcelTaxRows : []).reduce((sum, row) => {
    const insts = Array.isArray(row?.installments) ? row.installments : [];
    return sum + insts.reduce((s, i) => {
      const ov = i?.override;
      return ov && ov.adj_type === ABATEMENT && num(ov.adj_value) != null ? s + num(ov.adj_value) : s;
    }, 0);
  }, 0);
}

// One line's place on the bill. A line the extraction marked non_ad_valorem is non-ad valorem whatever it prints; else
// it is ad valorem when it carries a rate of 0–20 (reports/taxRate parseRateCell) or no rate but a taxable value and an
// amount (a rate lost to a wrap). Every other line with an amount is non-ad valorem.
function classify(l) {
  const amount = num(l?.amount);
  const rate = parseRateCell(l?.tax_rate);
  const taxable = num(l?.taxable_value);
  if (isNonAdValorem(l)) return { amount, rate: null, taxable: null, rated: false };
  const recovered = rate == null && taxable > 0 && amount > 0;
  return { amount, rate, taxable: taxable > 0 ? taxable : null, rated: rate != null || recovered };
}

/**
 * bill: { total_ad_valorem, total_non_ad_valorem, total_tax_rate, taxable_value, printed_total, discount_schedule, state,
 *         supplemental_seq, bill_type }
 * lines: the bill's jurisdiction lines ([{ name, section?, tax_rate, taxable_value, amount }])
 * opts:  { abatement } — the collector-year's Nevada abatement dollars (abatementOf), 0 when none
 */
export function checkBill(bill, lines, { abatement = 0 } = {}) {
  const flags = [];
  const add = (code, label, offBy, unit, detail = null, severity = 'problem') =>
    flags.push({ code, label, severity, offBy: Math.abs(offBy), unit, detail });
  const list = (Array.isArray(lines) ? lines : []).filter(Boolean);
  const rows = list.map((l) => ({ l, ...classify(l) }));
  const state = String(bill?.state || '').trim().toUpperCase();
  const av = num(bill?.total_ad_valorem);
  const nav = num(bill?.total_non_ad_valorem);
  const printed = num(bill?.printed_total);
  const supplemental = isSupplementalBill(bill);

  if (rows.length) {
    const avLines = rows.filter((r) => r.rated && r.amount != null).reduce((s, r) => s + r.amount, 0);
    const navLines = rows.filter((r) => !r.rated && r.amount != null).reduce((s, r) => s + r.amount, 0);
    const abate = num(abatement) || 0;
    const avExpected = avLines - abate;
    if (av != null && Math.abs(av - avExpected) > MONEY_TOLERANCE) {
      add('lines_av', 'Ad valorem lines ≠ total ad valorem', av - avExpected, '$',
        abate ? `lines ${avLines.toFixed(2)} − abatement ${abate.toFixed(2)} = ${avExpected.toFixed(2)}; bill ${av.toFixed(2)}`
          : `lines ${avLines.toFixed(2)}; bill ${av.toFixed(2)}`);
    }
    if (nav != null && Math.abs(nav - navLines) > MONEY_TOLERANCE) {
      add('lines_nav', 'Non-ad valorem lines ≠ total non-ad valorem', nav - navLines, '$', `lines ${navLines.toFixed(2)}; bill ${nav.toFixed(2)}`);
    }

    // The bill's unit: the lines' own arithmetic, else the state's (Florida / Hawaii print millage).
    const lineUnit = resolveRateUnit(list.filter((l) => !isNonAdValorem(l)));
    const div = (lineUnit.confident ? lineUnit.unit : (stateBillUnit(state) || 'percent')) === 'mills' ? 1000 : 100;
    const bad = rows.filter((r) => r.rate != null && r.taxable != null && r.amount != null
      && Math.abs(r.amount - (r.taxable * r.rate) / div) > MONEY_TOLERANCE);
    if (bad.length) {
      const worst = Math.max(...bad.map((r) => Math.abs(r.amount - (r.taxable * r.rate) / div)));
      add('line_math', bad.length === 1 ? 'A line’s amount ≠ taxable × rate' : `${bad.length} lines’ amounts ≠ taxable × rate`, worst, '$',
        bad.map((r) => `${r.l.name || 'unnamed'}: ${r.amount.toFixed(2)} vs ${((r.taxable * r.rate) / div).toFixed(2)}`).join('; '));
    }

    const rate = num(bill?.total_tax_rate);
    if (!supplemental && state !== 'IL' && rate != null) {
      const sumUnit = rows.filter((r) => r.rated).reduce((s, r) => s + (r.rate != null ? r.rate
        : r.taxable && r.amount != null ? (r.amount / r.taxable) * div : 0), 0);
      const sumPct = sumUnit * (100 / div);
      if (sumPct > 0 && Math.abs(rate - sumPct) > RATE_TOLERANCE) {
        add('rate', 'Total rate ≠ Σ ad valorem line rates', rate - sumPct, '%', `lines ${sumPct.toFixed(6)}%; bill ${rate}%`);
      }
    }

    // Property tax counted as non-ad valorem. A line the extraction marked ad_valorem but that has no rate is certain
    // (the bill prints it in the tax section); with no section marks, a no-rate line whose name reads like a levy is a
    // guess, listed as "check".
    const marked = list.some((l) => l.section != null);
    const lost = marked
      ? rows.filter((r) => !r.rated && String(r.l.section || '').trim().toLowerCase() === 'ad_valorem' && r.amount)
      : rows.filter((r) => !r.rated && r.amount && LEVY_NAME.test(String(r.l.name || '')));
    if (lost.length) {
      add('av_as_nav', marked ? 'Property tax counted as non-ad valorem (no rate)' : 'Possibly property tax counted as non-ad valorem',
        lost.reduce((s, r) => s + r.amount, 0), '$', lost.map((r) => `${r.l.name || 'unnamed'} ${r.amount.toFixed(2)}`).join('; '),
        marked ? 'problem' : 'check');
    }
  }

  if (!supplemental && printed != null && av != null) {
    const gross = av + (nav || 0);
    if (Math.abs(gross - printed) > MONEY_TOLERANCE) {
      add('printed', 'AV + NAV ≠ printed total', gross - printed, '$', `${av.toFixed(2)} + ${(nav || 0).toFixed(2)} = ${gross.toFixed(2)}; printed ${printed.toFixed(2)}`);
    }
    // The printed total equals an early-payment tier (or the gross less 1–4%): the discounted amount was read as the
    // bill's total. Only when it is below the gross — a printed total equal to the gross is the right reading.
    if (printed < gross - MONEY_TOLERANCE) {
      const tiers = (Array.isArray(bill?.discount_schedule) ? bill.discount_schedule : []).map((t) => num(t?.amount)).filter((a) => a != null);
      const pcts = [1, 2, 3, 4].map((p) => Math.round(gross * (1 - p / 100) * 100) / 100);
      if ([...tiers, ...pcts].some((a) => Math.abs(a - printed) <= MONEY_TOLERANCE)) {
        add('printed_discounted', 'Printed total looks like a discounted amount', gross - printed, '$',
          `printed ${printed.toFixed(2)} vs gross ${gross.toFixed(2)}`);
      }
    }
  }

  // No rate at all: no line rate (or one recovered from a line's own arithmetic), no printed rate the band accepts, and
  // no ad valorem ÷ taxable. Not on a $0 bill (ad valorem 0, no line amounts) — an exempt bill has no rate to find. A
  // bill with NO ad valorem entered is not a $0 one. offBy 0, unit null: not a size.
  const zeroBill = av === 0 && !rows.some((r) => r.amount);
  const lineRate = rows.some((r) => r.rated && (r.rate != null || (r.taxable && r.amount)));
  if (!zeroBill && !lineRate && printedBillRate(bill, { state }) == null
    && effectiveBillRate({ adValorem: av, taxable: bill?.taxable_value, state }) == null) {
    add('no_rate', 'No tax rate', 0, null,
      av > 0 ? 'no line rates or printed rate, and no taxable value to divide the ad valorem by (or the result is implausible)'
        : 'no line rates or printed rate, and no ad valorem tax to compute an effective rate from');
  }
  return flags;
}

// The report's $ size for a bill: its largest dollar flag (a rate flag counts by nothing — it has no dollar size).
export const dollarSize = (flags) => Math.max(0, ...(flags || []).filter((f) => f.unit === '$').map((f) => f.offBy));
